import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCcw } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Button, Card, Field, Modal, PageHead, Select, Spinner, useToast } from "../../components/ui";
import { FormattedMessage } from "react-intl";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import { useFmt } from "./format";

const ALL = "__all__";

/** "Clear generated designs": designs of the chosen organisations and builds regenerate on their next quote
 * (a new version with the current pipeline; stored imagery, no Lux). Nothing is deleted. */
export function AdminClearDesigns() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const { date } = useFmt();
  const [params] = useSearchParams();
  const [orgId, setOrgId] = useState<string>(params.get("org") ?? ALL);
  const [builds, setBuilds] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const orgs = useQuery({
    queryKey: ["admin-orgs", "clear-designs"],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs", { params: { query: { limit: 100 } } })),
  });
  const organization_id = orgId === ALL ? undefined : orgId;
  const list = useQuery({
    queryKey: ["design-builds", orgId],
    queryFn: () => unwrap(api.GET("/v1/admin/design-builds", { params: { query: { organization_id } } })),
  });
  useEffect(() => setBuilds(new Set()), [orgId]);
  const body: Omit<S["DesignsClear"], "dry_run"> = { organization_id: organization_id ?? null, builds: builds.size ? [...builds] : null };
  const preview = useQuery({
    queryKey: ["design-clear-preview", orgId, [...builds].sort().join(",")],
    queryFn: () => unwrap(api.POST("/v1/admin/designs/clear", { body: { ...body, dry_run: true } })),
  });
  const clear = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/admin/designs/clear", { body: { ...body, dry_run: false } })),
    onSuccess: (r) => {
      setConfirming(false);
      toast(t("admin.clearDesigns.done", { count: r.count }));
      qc.invalidateQueries({ queryKey: ["design-clear-preview"] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const toggle = (b: string) =>
    setBuilds((s) => {
      const next = new Set(s);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      return next;
    });
  const count = preview.data?.count;
  const orgName = orgs.data?.items.find((o) => o.organization.id === orgId)?.organization.name;
  const scope = t("admin.clearDesigns.scope", {
    org: orgId === ALL ? "all" : orgName ? "named" : "unknown",
    name: orgName ?? "",
    hasBuilds: builds.size ? "yes" : "no",
    builds: intl.formatList([...builds], { type: "conjunction" }),
  });
  const bold = (c: ReactNode[]) => <b>{c}</b>;

  return (
    <>
      <PageHead
        title={t("admin.clearDesigns.title")}
        description={t("admin.clearDesigns.description")}
      />
      <div className="stack">
        <Card title={t("admin.clearDesigns.scopeTitle")}>
          <Field label={t("admin.clearDesigns.organisation")}>
            <Select<string>
              value={orgId}
              onChange={setOrgId}
              options={[{ value: ALL, label: t("admin.clearDesigns.allOrganisations") }, ...(orgs.data?.items ?? []).map((o) => ({ value: o.organization.id, label: o.organization.name }))]}
            />
          </Field>
          <Field label={t("admin.clearDesigns.builds")} hint={t("admin.clearDesigns.buildsHint")}>
            {list.isLoading ? (
              <Spinner />
            ) : list.error || !list.data ? (
              <Alert tone="danger">{errorMessage(list.error)}</Alert>
            ) : list.data.items.length === 0 ? (
              <span className="muted small">{t("admin.clearDesigns.noDesigns")}</span>
            ) : (
              <div className="stack" style={{ gap: 6 }}>
                {list.data.items.map((b) => (
                  <label key={b.build} className="row" style={{ gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={builds.has(b.build)} onChange={() => toggle(b.build)} />
                    <span className="mono" dir="ltr">{b.build}</span>
                    <span className="small muted">
                      {t("admin.clearDesigns.buildLine", { count: b.designs, from: date(b.first_at), to: date(b.last_at) })}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Field>
        </Card>
        <Card>
          <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
            <span>
              {preview.isLoading ? (
                <Spinner />
              ) : (
                <FormattedMessage id="admin.clearDesigns.preview" values={{ count: count ?? 0, scope, b: bold }} />
              )}
            </span>
            <Button variant="danger" icon={<RefreshCcw />} disabled={!count || clear.isPending} onClick={() => setConfirming(true)}>
              {t("admin.clearDesigns.clear")}
            </Button>
          </div>
        </Card>
      </div>
      {confirming && (
        <Modal
          title={t("admin.clearDesigns.confirmTitle")}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <Button onClick={() => setConfirming(false)}>{t("admin.common.cancel")}</Button>
              <Button variant="danger" loading={clear.isPending} onClick={() => clear.mutate()}>
                {t("admin.clearDesigns.clearCount", { count: count ?? 0 })}
              </Button>
            </>
          }
        >
          <p>
            <FormattedMessage id="admin.clearDesigns.preview" values={{ count: count ?? 0, scope, b: bold }} />
          </p>
          <p>{t("admin.clearDesigns.confirmBody")}</p>
        </Modal>
      )}
    </>
  );
}
